import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import {
  Mail,
  CheckCircle2,
  Upload,
  Phone,
  Shield,
  Loader2,
  AlertCircle,
  CreditCard,
  Building2,
  FileCheck,
  Clock,
  MessageCircle,
  Smartphone,
  ExternalLink,
} from 'lucide-react';
import apiClient from '../services/api';
import StripeConnectSetup from '../components/payment/StripeConnectSetup';

const VerificationPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Verification status
  const [verificationStatus, setVerificationStatus] = useState({
    email_verified: false,
    phone_verified: false,
    kyc_status: 'not_submitted',
    verification_level: 'basic',
  });

  // Email verification
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailTestOtp, setEmailTestOtp] = useState('');

  // Phone verification
  const [phone, setPhone] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneTestOtp, setPhoneTestOtp] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('sms'); // 'sms' or 'whatsapp'

  // KYC form
  const [documentType, setDocumentType] = useState('passport');
  const [documentFile, setDocumentFile] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=verification');
      return;
    }
    fetchVerificationStatus();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Handle email verification from URL
    const token = searchParams.get('token');
    if (token) {
      verifyEmailWithToken(token);
    }
  }, [searchParams]);

  const fetchVerificationStatus = async () => {
    try {
      const response = await apiClient.get('/verification/kyc-status');
      setVerificationStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch verification status:', err);
    }
  };

  const verifyEmailWithToken = async (token) => {
    setLoading(true);
    try {
      await apiClient.post(`/verification/verify-email/${token}`);
      setSuccess('Email verified successfully!');
      fetchVerificationStatus();
    } catch (err) {
      setError('Invalid or expired verification link');
    } finally {
      setLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await apiClient.post('/verification/email/send-otp');
      if (response.data.already_verified) {
        setSuccess('Email is already verified!');
        fetchVerificationStatus();
      } else {
        setSuccess('Verification code sent to your email! Check your inbox.');
        // For testing - show test OTP if in mock mode
        if (response.data.test_otp) {
          setEmailTestOtp(response.data.test_otp);
        }
        setEmailOtpSent(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOTP = async () => {
    if (!emailOtp || emailOtp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await apiClient.post('/verification/email/verify-otp', { otp: emailOtp });
      setSuccess('Email verified successfully!');
      fetchVerificationStatus();
      setEmailOtpSent(false);
      setEmailOtp('');
      setEmailTestOtp('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneOTP = async () => {
    if (!phone || phone.length < 10) {
      setError('Please enter a valid phone number with country code (e.g., +1234567890)');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await apiClient.post('/verification/phone/send-otp', { 
        phone,
        method: verificationMethod // 'sms' or 'whatsapp'
      });
      if (response.data.already_verified) {
        setSuccess('Phone is already verified!');
        fetchVerificationStatus();
      } else {
        setPhoneOtpSent(true);
        const methodText = verificationMethod === 'whatsapp' ? 'WhatsApp' : 'SMS';
        setSuccess(`Verification code sent via ${methodText} to ${phone.slice(0,3)}****${phone.slice(-4)}`);
        // For testing - show test OTP if in mock mode
        if (response.data.test_otp) {
          setPhoneTestOtp(response.data.test_otp);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneOTP = async () => {
    if (!phoneOtp || phoneOtp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await apiClient.post('/verification/phone/verify-otp', { phone, otp: phoneOtp });
      setSuccess('Phone verified successfully!');
      fetchVerificationStatus();
      setPhoneOtpSent(false);
      setPhoneOtp('');
      setPhoneTestOtp('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const uploadKYCDocument = async () => {
    if (!documentFile) {
      setError('Please select a document to upload');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      // First, upload the file to R2 storage
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('document_type', documentType);
      
      const uploadResponse = await apiClient.post('/r2/upload/kyc', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (!uploadResponse.data.success) {
        throw new Error('Failed to upload document to storage');
      }
      
      // Then register the document with verification service
      await apiClient.post('/verification/upload-kyc', {
        document_type: documentType,
        document_url: uploadResponse.data.key, // Store the R2 key, not public URL
      });
      
      setSuccess('Document uploaded! Our team will review it within 1-2 business days.');
      setDocumentFile(null);
      fetchVerificationStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const getProgress = () => {
    let progress = 0;
    if (verificationStatus.email_verified) progress += 33;
    if (verificationStatus.phone_verified) progress += 33;
    if (verificationStatus.kyc_status === 'approved') progress += 34;
    else if (verificationStatus.kyc_status === 'pending') progress += 17;
    return progress;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-4">
            <h1 className="text-2xl font-bold text-foreground">Account Verification</h1>
            <p className="text-muted-foreground text-sm">Complete these steps to activate your campaigns and receive donations</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4 max-w-md mx-auto">
            <Progress value={getProgress()} className="h-2" />
            <p className="text-center text-xs text-muted-foreground mt-1">{getProgress()}% Complete</p>
          </div>

          {error && (
            <Alert className="mb-6 bg-red-500/10 border-red-500/30">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-red-400">{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-4 bg-emerald-500/10 border-emerald-500/30">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <AlertDescription className="text-emerald-400">{success}</AlertDescription>
            </Alert>
          )}

          {/* 3-Column Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Step 1: Email Verification */}
            <Card className={`${verificationStatus.email_verified ? 'border-emerald-500/50' : ''} flex flex-col`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      verificationStatus.email_verified ? 'bg-emerald-500' : 'bg-secondary'
                    }`}>
                      {verificationStatus.email_verified ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : (
                        <Mail className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <span>Email</span>
                  </CardTitle>
                  {verificationStatus.email_verified && (
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Done
                    </span>
                  )}
                </div>
                <CardDescription className="text-xs">Verify your email address</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pt-2">
                {verificationStatus.email_verified ? (
                  <p className="text-emerald-400 flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    Email verified
                  </p>
                ) : emailOtpSent ? (
                  <div className="space-y-3">
                    <p className="text-muted-foreground text-xs">
                      Enter code sent to <strong className="text-foreground">{user?.email}</strong>
                    </p>
                    {emailTestOtp && (
                      <Alert className="bg-amber-500/10 border-amber-500/30 py-2">
                        <AlertDescription className="text-amber-300 text-xs">
                          Test code: <span className="font-mono font-bold">{emailTestOtp}</span>
                        </AlertDescription>
                      </Alert>
                    )}
                    <Input
                      type="text"
                      placeholder="000000"
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="font-mono tracking-widest text-center"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={verifyEmailOTP}
                        disabled={loading || emailOtp.length !== 6}
                        size="sm"
                        className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600"
                      >
                        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setEmailOtpSent(false); setEmailOtp(''); setEmailTestOtp(''); }}
                      >
                        Resend
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-muted-foreground text-xs">
                      Send code to <strong className="text-foreground">{user?.email}</strong>
                    </p>
                    <Button
                      onClick={sendVerificationEmail}
                      disabled={loading}
                      size="sm"
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-600"
                    >
                      {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                      Send Code
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Step 2: Phone Verification */}
            <Card className={`${verificationStatus.phone_verified ? 'border-emerald-500/50' : ''} flex flex-col`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      verificationStatus.phone_verified ? 'bg-emerald-500' : 'bg-secondary'
                    }`}>
                      {verificationStatus.phone_verified ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : (
                        <Phone className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <span>Phone</span>
                  </CardTitle>
                  {verificationStatus.phone_verified && (
                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Done
                    </span>
                  )}
                </div>
                <CardDescription className="text-xs">Verify your phone number</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pt-2">
                {verificationStatus.phone_verified ? (
                  <p className="text-emerald-400 flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    Phone verified
                  </p>
                ) : (
                  <div className="space-y-3">
                    {!phoneOtpSent ? (
                      <>
                        <div>
                          <div className="flex gap-2">
                            {/* Country Code Selector with Flag */}
                            <select
                              value={phone.split(' ')[0] || '+1'}
                              onChange={(e) => {
                                const phoneNum = phone.split(' ').slice(1).join(' ') || '';
                                setPhone(`${e.target.value} ${phoneNum}`);
                              }}
                              className="w-28 h-9 rounded-md border-2 border-emerald-500/50 bg-secondary px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                              <option value="+1">🇺🇸 +1</option>
                              <option value="+44">🇬🇧 +44</option>
                              <option value="+91">🇮🇳 +91</option>
                              <option value="+61">🇦🇺 +61</option>
                              <option value="+49">🇩🇪 +49</option>
                              <option value="+33">🇫🇷 +33</option>
                              <option value="+81">🇯🇵 +81</option>
                              <option value="+86">🇨🇳 +86</option>
                              <option value="+55">🇧🇷 +55</option>
                              <option value="+52">🇲🇽 +52</option>
                              <option value="+82">🇰🇷 +82</option>
                              <option value="+39">🇮🇹 +39</option>
                              <option value="+34">🇪🇸 +34</option>
                              <option value="+7">🇷🇺 +7</option>
                              <option value="+971">🇦🇪 +971</option>
                              <option value="+966">🇸🇦 +966</option>
                              <option value="+65">🇸🇬 +65</option>
                              <option value="+60">🇲🇾 +60</option>
                              <option value="+27">🇿🇦 +27</option>
                              <option value="+234">🇳🇬 +234</option>
                            </select>
                            {/* Phone Number Input */}
                            <Input
                              type="tel"
                              placeholder="Phone number"
                              value={phone.split(' ').slice(1).join(' ') || ''}
                              onChange={(e) => {
                                const code = phone.split(' ')[0] || '+1';
                                setPhone(`${code} ${e.target.value.replace(/[^0-9]/g, '')}`);
                              }}
                              className="flex-1 bg-secondary text-sm border-2 border-emerald-500/50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                        {/* SMS / WhatsApp selector */}
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={verificationMethod === 'sms' ? 'default' : 'outline'}
                            onClick={() => setVerificationMethod('sms')}
                            className={verificationMethod === 'sms' ? 'flex-1 bg-emerald-600' : 'flex-1'}
                          >
                            <Smartphone className="w-3 h-3 mr-1" /> SMS
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={verificationMethod === 'whatsapp' ? 'default' : 'outline'}
                            onClick={() => setVerificationMethod('whatsapp')}
                            className={verificationMethod === 'whatsapp' ? 'flex-1 bg-green-600' : 'flex-1'}
                          >
                            <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                          </Button>
                        </div>
                        <Button
                          onClick={sendPhoneOTP}
                          disabled={loading || !phone}
                          size="sm"
                          className={`w-full ${verificationMethod === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' : 'bg-gradient-to-r from-emerald-500 to-teal-600'}`}
                        >
                          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : verificationMethod === 'whatsapp' ? <MessageCircle className="w-3 h-3 mr-1" /> : <Phone className="w-3 h-3 mr-1" />}
                          Send via {verificationMethod === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Input
                          type="text"
                          placeholder="000000"
                          maxLength={6}
                          value={phoneOtp}
                          onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, ''))}
                          className="bg-secondary text-center tracking-widest font-mono"
                        />
                        {phoneTestOtp && (
                          <Alert className="bg-amber-500/10 border-amber-500/30 py-2">
                            <AlertDescription className="text-amber-300 text-xs">
                              Test code: <span className="font-mono font-bold">{phoneTestOtp}</span>
                            </AlertDescription>
                          </Alert>
                        )}
                        <div className="flex gap-2">
                          <Button
                            onClick={verifyPhoneOTP}
                            disabled={loading || phoneOtp.length !== 6}
                            size="sm"
                            className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600"
                          >
                            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setPhoneOtpSent(false); setPhoneOtp(''); setPhoneTestOtp(''); }}>
                            Change
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Step 3: Document Verification */}
            <Card className={`${verificationStatus.kyc_status === 'approved' ? 'border-emerald-500/50' : ''} flex flex-col`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      verificationStatus.kyc_status === 'approved' ? 'bg-emerald-500' : 
                      verificationStatus.kyc_status === 'pending' ? 'bg-yellow-500' : 'bg-secondary'
                    }`}>
                      {verificationStatus.kyc_status === 'approved' ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : verificationStatus.kyc_status === 'pending' ? (
                        <Clock className="w-4 h-4 text-white" />
                      ) : (
                        <FileCheck className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <span>KYC</span>
                  </CardTitle>
                  {verificationStatus.kyc_status === 'approved' && (
                    <span className="text-sm text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Verified
                    </span>
                  )}
                  {verificationStatus.kyc_status === 'pending' && (
                    <span className="text-xs text-yellow-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Review
                    </span>
                  )}
                </div>
                <CardDescription className="text-xs">Upload government ID</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pt-2">
                {verificationStatus.kyc_status === 'approved' ? (
                  <p className="text-emerald-400 flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    Identity verified
                  </p>
                ) : verificationStatus.kyc_status === 'pending' ? (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-yellow-400 flex items-center gap-2 text-xs">
                      <Clock className="w-4 h-4" />
                      Under review (1-2 days)
                    </p>
                  </div>
                ) : verificationStatus.kyc_status === 'rejected' ? (
                  <div className="space-y-3">
                    <Alert className="bg-red-500/10 border-red-500/30 py-2">
                      <AlertDescription className="text-red-400 text-xs">
                        Rejected: {verificationStatus.kyc_rejection_reason || 'Documents unclear'}
                      </AlertDescription>
                    </Alert>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <select
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                      className="w-full p-2 rounded-md bg-secondary border-2 border-emerald-500/40 text-sm focus:outline-none focus:border-emerald-500 transition-all"
                    >
                      <option value="passport">Passport</option>
                      <option value="national_id">National ID</option>
                      <option value="drivers_license">Driver License</option>
                    </select>
                    
                    <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                      <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                      <p className="text-xs text-muted-foreground mb-2">
                        PNG, JPG, PDF (max 10MB)
                      </p>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setDocumentFile(e.target.files[0])}
                        className="hidden"
                        id="doc-upload"
                      />
                      <Button variant="outline" size="sm" onClick={() => document.getElementById('doc-upload').click()}>
                        Select File
                      </Button>
                      {documentFile && (
                        <p className="text-xs text-emerald-400 mt-2 truncate">{documentFile.name}</p>
                      )}
                    </div>

                    <Button
                      onClick={uploadKYCDocument}
                      disabled={loading || !documentFile}
                      size="sm"
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-600"
                    >
                      {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                      Submit
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stripe Connect for Payouts - Full Width Below */}
          {verificationStatus.kyc_status === 'approved' && (
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center bg-secondary">
                    <CreditCard className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span>Payout Setup via Stripe</span>
                </CardTitle>
                <CardDescription className="text-xs">Connect your Stripe account to receive withdrawals securely</CardDescription>
              </CardHeader>
              <CardContent>
                <StripeConnectSetup />
              </CardContent>
            </Card>
          )}

          {/* Back to Dashboard */}
          <div className="mt-4 text-center">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default VerificationPage;
